import React, { useMemo, useState } from 'react';
import { Box, Grid, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import { PickersDay, StaticDatePicker } from '@mui/x-date-pickers';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { AccountData } from '../../../api';
import { useAvailableDates } from '../../../orderPage/multipleOrderPages/useAvailableDates';
import { getExperienceName } from '../../../managePassesPage/utils/passUtils';
import { getTimeFormat } from '../../../utils/getDateAndTimeFormat';
import { useLanguage } from '../../../utils/GeneralUtils';
import RotatingLoader from '../../../ui/RotatingLoader';
import { DATE_FORMAT } from '../../../consts';

const WORKSHOP_COLORS = ['#0e7c6b', '#d97706', '#2563eb', '#be185d', '#7c3aed'];

type SessionInfo = {
  timeSeconds: number;
  duration: number;
  workshopName: string;
  workshopIndex: number;
};

const getOrderPageDuration = (account: AccountData, orderPageId: string): number => {
  for (const exp of account.experiences || []) {
    const op = exp.order_pages?.find((p) => p.id === orderPageId);
    if (op) return op.duration || 0;
  }
  return 0;
};

const ScheduleDay = ({ day, sessionsByDate, outsideCurrentMonth, ...pickersDayProps }: any) => {
  const dateStr = moment(day).format('YYYY-MM-DD');
  const sessions: SessionInfo[] = sessionsByDate.get(dateStr) || [];
  const dotIndices = outsideCurrentMonth ? [] : [...new Set(sessions.map((s) => s.workshopIndex))];

  return (
    <Box sx={{ position: 'relative' }}>
      <PickersDay {...pickersDayProps} day={day} outsideCurrentMonth={outsideCurrentMonth} />
      {dotIndices.length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 5,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            gap: '2px',
            pointerEvents: 'none',
          }}>
          {dotIndices.map((idx) => (
            <Box
              key={idx}
              sx={{
                width: 4,
                height: 4,
                borderRadius: '50%',
                bgcolor: WORKSHOP_COLORS[idx % WORKSHOP_COLORS.length],
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

type PassScheduleSectionProps = {
  orderPageId: string;
  account: AccountData;
};

export const PassScheduleSection = ({ orderPageId, account }: PassScheduleSectionProps) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const timeFormat = getTimeFormat(account);

  const passOrderPage = account.pass_order_pages?.find((p) => p.id === orderPageId);
  const orderPageIds = useMemo(() => passOrderPage?.redeemable_on?.map((r) => r.order_page_id) || [], [passOrderPage]);

  const workshopNames = useMemo(
    () => orderPageIds.map((id) => getExperienceName(account, id, language)),
    [account, orderPageIds, language],
  );

  const { availableDates, isLoading } = useAvailableDates(account.id || '', orderPageIds, 1);

  const sessionsByDate = useMemo(() => {
    const map = new Map<string, SessionInfo[]>();
    for (const opAvailability of availableDates) {
      const workshopIndex = orderPageIds.indexOf(opAvailability.order_page_id);
      const workshopName = workshopIndex >= 0 ? workshopNames[workshopIndex] : '';
      const duration = getOrderPageDuration(account, opAvailability.order_page_id);
      for (const dayAvailability of opAvailability.availability) {
        if (!dayAvailability.isDisabled) {
          for (const slot of dayAvailability.times) {
            if (!slot.isDisabled) {
              const existing = map.get(dayAvailability.date) || [];
              existing.push({ timeSeconds: slot.seconds, duration, workshopName, workshopIndex });
              map.set(dayAvailability.date, existing);
            }
          }
        }
      }
    }
    for (const sessions of map.values()) {
      sessions.sort((a, b) => a.timeSeconds - b.timeSeconds);
    }
    return map;
  }, [availableDates, orderPageIds, workshopNames, account]);

  const firstAvailableDate = useMemo(() => {
    const today = moment().format('YYYY-MM-DD');
    for (const date of [...sessionsByDate.keys()].sort()) {
      if (date >= today) return moment(date);
    }
    return moment();
  }, [sessionsByDate]);

  const [selectedDate, setSelectedDate] = useState<moment.Moment | null>(null);
  const effectiveSelectedDate = selectedDate ?? firstAvailableDate;

  const selectedSessions = useMemo(
    () => sessionsByDate.get(effectiveSelectedDate.format('YYYY-MM-DD')) || [],
    [effectiveSelectedDate, sessionsByDate],
  );

  const maxDate = moment().add(1, 'month').endOf('month');

  if (!passOrderPage || orderPageIds.length === 0) return null;

  if (isLoading) {
    return (
      <Grid container justifyContent="center" sx={{ py: 4 }}>
        <RotatingLoader />
      </Grid>
    );
  }

  if (sessionsByDate.size === 0) return null;

  if (orderPageIds.length <= 2) {
    const today = moment().format(DATE_FORMAT);
    const sortedDates = [...sessionsByDate.keys()].filter((d) => d >= today).sort();
    return (
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 3,
          p: isSmallScreen ? 2 : 3,
          mb: isSmallScreen ? 4 : 5,
          width: isSmallScreen ? '100%' : 320,
        }}>
        <Grid container direction="column" spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6" fontWeight={700}>
              {t('pass_experience.schedule.title')}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                maxHeight: 360,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                pr: 0.5,
              }}>
              {sortedDates.map((dateStr) => {
                const sessions = sessionsByDate.get(dateStr) || [];
                return (
                  <Box key={dateStr}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                      sx={{ mb: 0.75, display: 'block' }}>
                      {moment(dateStr).format('dddd, MMMM D')}
                    </Typography>
                    <Grid container direction="column" spacing={0.75}>
                      {sessions.map((session, idx) => {
                        const startTime = moment.utc(session.timeSeconds * 1000).format(timeFormat);
                        const endTime = moment.utc((session.timeSeconds + session.duration) * 1000).format(timeFormat);
                        return (
                          <Grid item xs={12} key={idx}>
                            <Box
                              sx={{
                                py: 1.25,
                                px: 1.75,
                                borderRadius: 1.5,
                                bgcolor: 'action.hover',
                                borderLeft: `3px solid ${
                                  WORKSHOP_COLORS[session.workshopIndex % WORKSHOP_COLORS.length]
                                }`,
                              }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {session.workshopName}
                              </Typography>
                              <Typography variant="body2" fontWeight={500}>
                                {startTime}
                                {session.duration > 0 && ` – ${endTime}`}
                              </Typography>
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                );
              })}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 3,
        p: isSmallScreen ? 2 : 3,
        mb: isSmallScreen ? 4 : 5,
        width: isSmallScreen ? '100%' : 'fit-content',
      }}>
      <Grid container direction="column" spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6" fontWeight={700}>
            {t('pass_experience.schedule.title')}
          </Typography>
        </Grid>

        {workshopNames.length > 1 && (
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block' }}>
              {t('pass_experience.schedule.included')}
            </Typography>
            <Grid container spacing={1}>
              {workshopNames.map((name, idx) => (
                <Grid item key={name} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Grid
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: WORKSHOP_COLORS[idx % WORKSHOP_COLORS.length],
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: '15ch' }}>
                    {name}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}

        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ borderRadius: 2, pb: 1 }}>
            <StaticDatePicker
              displayStaticWrapperAs="desktop"
              disablePast
              value={effectiveSelectedDate}
              minDate={moment()}
              maxDate={maxDate}
              shouldDisableDate={(date) => !sessionsByDate.has(moment(date).format('YYYY-MM-DD'))}
              slots={{ day: ScheduleDay as any }}
              slotProps={{ day: { sessionsByDate } as any }}
              onChange={(date) => setSelectedDate(date ? moment(date) : null)}
              sx={{
                width: '100%',
                '.MuiDateCalendar-root': { overflow: 'visible', width: '100%', maxWidth: '100%' },
                '.MuiDayCalendar-slideTransition': { overflow: 'visible' },
                '.MuiDayCalendar-weekContainer': { overflow: 'visible' },
                '.MuiPickersDay-root.Mui-selected': {
                  bgcolor: 'grey.100',
                  color: 'text.primary',
                  border: '2px dashed black',
                  '&:hover': { bgcolor: 'grey.200' },
                  '&:focus': { bgcolor: 'grey.100' },
                },
              }}
            />
          </Paper>
        </Grid>

        {selectedSessions.length > 0 && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              {effectiveSelectedDate.format('dddd, MMMM D')}
            </Typography>
            <Grid container direction="column" spacing={1}>
              {selectedSessions.map((session, idx) => {
                const startTime = moment.utc(session.timeSeconds * 1000).format(timeFormat);
                const endTime = moment.utc((session.timeSeconds + session.duration) * 1000).format(timeFormat);
                const color = WORKSHOP_COLORS[session.workshopIndex % WORKSHOP_COLORS.length];
                return (
                  <Grid item xs={12} key={idx}>
                    <Grid
                      sx={{
                        py: 1,
                        px: 1.5,
                        borderRadius: 1.5,
                        bgcolor: 'action.hover',
                        borderLeft: `3px solid ${color}`,
                      }}>
                      {workshopNames.length > 1 && (
                        <Typography variant="caption" color="text.secondary">
                          {session.workshopName}
                        </Typography>
                      )}
                      <Typography variant="body2" fontWeight={500}>
                        {startTime}
                        {session.duration > 0 && ` – ${endTime}`}
                      </Typography>
                    </Grid>
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};
